import { useCallback } from 'react';

import { stringifyObject } from 'pages/hotels/component/Search/__helper/stringify';

import { defaultPagination } from 'config/SearchForm/defaults';

import { useResetChangedFilterList } from 'hooks/form/useChangedFilterList';

import { filterChanged } from 'data/Atom/Component';
import { filterAtom } from 'data/Atom/Filter';
import {
    baseSearchFormAtom,
    currentSearchAtom,
    destinationLabelAtom,
    occupancyAtom,
    paginationAtom,
} from 'data/Atom/Search';
import { filterStore, formStore, searchStore } from 'data/Atom/Store';

import Console from 'utility/Debug/Console';

export const useSync = () => {
    const resetChangedFilterList = useResetChangedFilterList();

    const hasFilterChanged = useCallback(() => {
        const outerState = searchStore.get(baseSearchFormAtom);
        const currentFilter = filterStore.get(filterAtom);
        const jsonCurrentFilter = stringifyObject(currentFilter);
        const jsonOuterFilter = stringifyObject(outerState.current.filter);

        return jsonCurrentFilter !== jsonOuterFilter;
    }, []);

    /**
     * sync the global searchStore with filterStore
     */
    const syncSearchWithFilterStore = useCallback(() => {
        // write filter into searchStore
        const outerState = searchStore.get(baseSearchFormAtom);
        const currentFilter = filterStore.get(filterAtom);
        // set global searchStore if filter differs from internal
        if (hasFilterChanged()) {
            searchStore.set(filterChanged, true);
            searchStore.set(baseSearchFormAtom, {
                ...outerState,
                current: {
                    ...outerState.current,
                    pagination: {
                        ...outerState.current.pagination,
                        currentPage: 1,
                    },
                    filter: currentFilter,
                },
            });
        }
    }, [hasFilterChanged]);

    /**
     * sync the global searchStore with formStore
     */
    const syncSearchWithFormStore = useCallback(() => {
        // write formStore to global searchStore
        const searchState = searchStore.get(baseSearchFormAtom);
        const formState = formStore.get(baseSearchFormAtom);

        // set global searchStore
        searchStore.set(baseSearchFormAtom, {
            ...searchState,
            current: {
                ...searchState.current,
                ...formState.current,
                filter: searchState.current.filter,
                pagination: defaultPagination,
            },
            recent: searchState.current,
        });
    }, []);

    /**
     * sync the global searchStore with formStore
     */
    const syncSearchWithFormAndFilterStore = useCallback(() => {
        // write formStore to global searchStore
        const searchState = searchStore.get(baseSearchFormAtom);
        const formState = formStore.get(baseSearchFormAtom);
        const currentFilter = filterStore.get(filterAtom);

        // set global searchStore if filter differs from internal
        if (hasFilterChanged()) {
            searchStore.set(filterChanged, true);
            searchStore.set(baseSearchFormAtom, {
                ...searchState,
                current: {
                    ...searchState.current,
                    ...formState.current,
                    pagination: {
                        ...searchState.current.pagination,
                        currentPage: 1,
                    },
                    filter: currentFilter,
                },
                recent: searchState.current,
            });
        }
    }, [hasFilterChanged]);

    /**
     * sync sub stores with global searchStore
     */
    const syncSubStoresWithCurrentSearch = useCallback(
        (onlyMetaUpdate = false) => {
            const currentSearch = searchStore.get(currentSearchAtom);
            if (onlyMetaUpdate) {
                Console.trace('meta sync triggered');
                formStore.set(
                    destinationLabelAtom,
                    currentSearch.destination.label
                );
                formStore.set(paginationAtom, currentSearch.pagination);

                return;
            }
            Console.trace('current search sync triggered');
            // sync to formStore
            formStore.set(baseSearchFormAtom, (currentFormState) => ({
                ...currentFormState,
                current: currentSearch,
                recent: null,
            }));

            if (hasFilterChanged()) {
                searchStore.set(filterChanged, true);
            }

            // sync to filterStore
            filterStore.set(filterAtom, currentSearch.filter);
            // sync occupancy to filterStore for children-filter
            filterStore.set(occupancyAtom, currentSearch.occupancy);
        },
        [hasFilterChanged]
    );

    // only used for manual filter submit feature
    const syncFilterAndSearchWithFormStore = useCallback(() => {
        // write filter into searchStore
        const currentFilter = filterStore.get(filterAtom);
        const searchState = searchStore.get(baseSearchFormAtom);
        const formState = formStore.get(baseSearchFormAtom);
        resetChangedFilterList();
        // set global searchStore if filter differs from internal
        // write formStore to global searchStore
        if (hasFilterChanged()) {
            searchStore.set(filterChanged, true);
            searchStore.set(baseSearchFormAtom, {
                ...searchState,
                current: {
                    ...searchState.current,
                    ...formState.current,
                    pagination: {
                        ...searchState.current.pagination,
                        currentPage: 1,
                    },
                    filter: currentFilter,
                },
                recent: searchState.current,
            });
        } else {
            syncSearchWithFormStore();
        }
    }, [hasFilterChanged, syncSearchWithFormStore, resetChangedFilterList]);

    return {
        syncSearchWithFilterStore,
        syncSearchWithFormStore,
        syncSubStoresWithCurrentSearch,
        syncFilterAndSearchWithFormStore,
        syncSearchWithFormAndFilterStore,
    };
};
